import {
  DIRECT_LINE_NAME,
  DYNAMIC_CALLER_ID_NAME,
  DYNAMIC_CALLER_ID_NUMBER,
} from '@components/OutboundCallerIdSelection/constants';
import useAuth from '@modules/AuthModule/hooks/useAuth';
import { useCallback, useEffect, useState } from 'react';
import { DIRECT_LINE } from 'src/constants/tenantSelection';
import { useTenantOutboundConfig } from './useTenantOutboundConfig';
import { AgentCallerIdDetails } from './types';

export const useAgentLine = ({
  tenantId,
  onLoadingChange,
  onChange,
  isDynamic,
}: {
  tenantId?: string;
  onLoadingChange?: (loading: boolean) => void;
  onChange: (data: AgentCallerIdDetails) => void;
  isDynamic?: boolean;
}) => {
  const { user } = useAuth();
  const directLine = user?.personalLine?.phoneNumber;
  const isTenantSelected = tenantId !== DIRECT_LINE;
  const [selectedLine, setSelectedLine] = useState<{
    callerId: string;
    callerName?: string;
  } | null>(null);
  const {
    outgoingCallerId,
    enableCustomOutboundCallerId,
    isPermissionToUseDID,
    loadingCallerId,
    error,
  } = useTenantOutboundConfig({
    selectedTenantId: tenantId,
  });
  const isMultipleLines = isTenantSelected && !!outgoingCallerId && isDynamic;

  useEffect(() => {
    onLoadingChange?.(loadingCallerId);
  }, [loadingCallerId, onLoadingChange]);

  const calculateSelectedLine = useCallback(() => {
    if (!isTenantSelected) {
      if (isPermissionToUseDID && directLine) {
        return {
          callerId: directLine,
          callerName: DIRECT_LINE_NAME,
        };
      }

      if (!isPermissionToUseDID) {
        return {
          callerId: '',
          callerName: 'No permissions to use direct line',
        };
      }

      return {
        callerId: '',
        callerName: 'No direct line setup for this agent',
      };
    }

    if (outgoingCallerId) {
      return {
        callerId: outgoingCallerId ?? '',
        callerName: '',
      };
    } else {
      return {
        callerId: DYNAMIC_CALLER_ID_NUMBER,
        callerName: DYNAMIC_CALLER_ID_NAME,
      };
    }
  }, [isTenantSelected, isPermissionToUseDID, directLine, outgoingCallerId]);

  const onLineChange = useCallback(
    (data: AgentCallerIdDetails) => {
      setSelectedLine(data);
      if (data?.callerId) {
        onChange({
          callerId: data.callerId,
          callerName: data.callerName ?? undefined,
        });
      }
    },
    [onChange],
  );

  useEffect(() => {
    if (!loadingCallerId) {
      const newSelectedLine = calculateSelectedLine();
      onLineChange(newSelectedLine);
    }
  }, [calculateSelectedLine, loadingCallerId, onLineChange]);

  return {
    selectedLine,
    showChangeButton: !!enableCustomOutboundCallerId || isMultipleLines,
    outgoingCallerId,
    enableCustomOutboundCallerId,
    isPermissionToUseDID,
    loadingCallerId,
    onLineChange,
    error,
  };
};
